import classNames from 'classnames';
import { useCallback, useMemo } from 'react';

import camelCase from 'lodash.camelcase';
import { ReactstrapUncontrolledTooltip as UncontrolledTooltip } from './ReactstrapWrapper';
import Icon, { IconErrorBoundary } from "./Icon";

export interface ButtonProps {
  id?: string;
  title?: string;
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  className?: string;
  style?: React.CSSProperties;
  icon?: string;
  swap?: boolean;
  classIcon?: string;
  type?: 'button' | 'submit' | 'reset';
  size?: 'sm' | 'lg';
  theme?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark';
  outline?: boolean;
  loading?: boolean;
  disabled?: boolean;
  rounded?: boolean;
  role?: string;
  buttonProps?: React.ButtonHTMLAttributes<HTMLButtonElement> | object;
  iconProps?: {
    [key: string]: string;
  };
  innerRef?: React.Ref<HTMLButtonElement>;
}

export function Button({
  id: defaultId,
  title,
  children,
  onClick,
  className,
  style = {},
  icon,
  swap = false,
  classIcon,
  type = 'button',
  size,
  theme = 'primary',
  outline = false,
  loading = false,
  disabled = false,
  rounded = false,
  role,
  buttonProps = {},
  iconProps = {},
  innerRef
}: ButtonProps) {
  const id = useMemo(() => {
    return defaultId ?? camelCase(`button-${title}`);
  }, [defaultId, title]);

  const renderIcon = useMemo(() => {
      try {
        const useIcon = icon ?? loading;
        if (!useIcon) {
          return null;
        }
        if (loading) {
          return (
            <Icon
              icon={'spinner'}
              spin={loading}
              className={classNames(classIcon, !!children && 'me-2')}
              {...iconProps}
            />
          );
        } else if (icon && !loading) {
          return (
            <Icon
              icon={icon}
              spin={loading}
              className={classNames(classIcon, !!children && 'me-2')}
              {...iconProps}
            />
          );
        } else {
          return undefined;
        }
      }catch (e) {
        console.error(e);
        return null;
      }
    },
    [children, classIcon, icon, iconProps, loading]
  );
  return (
    <>
      <button
        id={id}
        type={type}
        disabled={loading || disabled}
        className={classNames(
          'btn',
          `btn-${outline ? 'outline-' : ''}${theme}`,
          !children && 'btn-icon',
          size && `btn-${size}`,
          rounded && 'btn-rounded',
          className
        )}
        ref={innerRef}
        style={style}
        {...(onClick && { onClick })}
        {...(role && { role })}
        {...buttonProps}
      >
        <IconErrorBoundary>
        {!swap && renderIcon ? (renderIcon) : null}
        </IconErrorBoundary>
        {children}
        <IconErrorBoundary>
        {swap && renderIcon  ? (renderIcon) : null}
        </IconErrorBoundary>
      </button>
      {title && <UncontrolledTooltip target={id}>{title}</UncontrolledTooltip>}
    </>
  );
}

export default Button;
